import { useId, type ChangeEvent, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cx } from '@/lib/cx';
import { Icon, type IconName } from './Icon';
import s from './Field.module.css';

/** A caps section label (ОРГАНИЗАЦИЯ, ТИП САЙТА…): 12px, or 11px in drawers and sheets. */
export function CapsLabel({ children, small, id }: { children: ReactNode; small?: boolean; id?: string }) {
  return (
    <span id={id} className={cx(s.caps, small && s.caps11)}>
      {children}
    </span>
  );
}

export function FieldHint({ children }: { children: ReactNode }) {
  return <span className={s.hint}>{children}</span>;
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & { label?: ReactNode; height?: 46 | 44; mono?: boolean };

/** Text input on #0D0D0F, 46px, radius 14; with an optional 12.5px label above. */
export function Input({ label, height = 46, mono, className, ...rest }: InputProps) {
  const input = <input className={cx(s.input, height === 44 && s.input44, mono && s.mono, className)} {...rest} />;
  if (!label) return input;
  return (
    <label className={s.field}>
      <span className={s.label}>{label}</span>
      {input}
    </label>
  );
}

/** Input with a leading icon (the research-source inputs). */
export function IconInput({ icon, className, ...rest }: InputHTMLAttributes<HTMLInputElement> & { icon: IconName }) {
  return (
    <div className={cx(s.iconBox, className)}>
      <Icon name={icon} size={15} className={s.iconBoxIcon} />
      <input className={s.bare} {...rest} />
    </div>
  );
}

/** The toolbar search pill (40px, max 380px). */
export function SearchInput({
  value,
  onChange,
  placeholder,
  label,
  className,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
  className?: string;
}) {
  return (
    <div className={cx(s.search, className)}>
      <Icon name="search" size={15} />
      <input
        type="text"
        role="searchbox"
        aria-label={label}
        className={s.bare}
        value={value}
        placeholder={placeholder}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      />
    </div>
  );
}

type TextareaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: ReactNode; mono?: boolean };

export function Textarea({ label, mono, className, ...rest }: TextareaProps) {
  const el = <textarea className={cx(s.textarea, mono && s.mono, className)} {...rest} />;
  if (!label) return el;
  return (
    <label className={cx(s.field, s.fieldTight)}>
      <span className={s.label}>{label}</span>
      {el}
    </label>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  icon?: IconName;
  options: { value: string; label: string }[];
};

/** Native select styled as the create-site organization picker (48px, accent icon, chevron). */
export function Select({ icon, options, className, ...rest }: SelectProps) {
  return (
    <div className={cx(s.select, className)}>
      {icon && <Icon name={icon} size={16} className={s.selectIcon} />}
      <select className={s.selectEl} {...rest}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <Icon name="chevron-down" size={16} className={s.selectChevron} />
    </div>
  );
}

/** Dashed upload tile («Логотип · SVG/PNG», «Файл каталога»). */
export function FileTile({
  icon,
  children,
  accept,
  onFiles,
}: {
  icon: IconName;
  children: ReactNode;
  accept?: string;
  onFiles?: (files: File[]) => void;
}) {
  const id = useId();
  return (
    <label htmlFor={id} className={s.fileTile}>
      <Icon name={icon} size={18} />
      {children}
      <input
        id={id}
        type="file"
        accept={accept}
        className={s.fileInput}
        onChange={(e) => {
          onFiles?.(Array.from(e.target.files ?? []));
          e.target.value = '';
        }}
      />
    </label>
  );
}

/** A selectable option card (create site: type, mode). Use inside a role="radiogroup". */
export function OptionCard({
  icon,
  title,
  text,
  selected,
  onSelect,
}: {
  icon: IconName;
  title: ReactNode;
  text: ReactNode;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button type="button" role="radio" aria-checked={selected} className={s.option} onClick={onSelect}>
      <Icon name={icon} size={20} className={s.optionIcon} />
      <span className={s.optionTitle}>{title}</span>
      <span className={s.optionText}>{text}</span>
    </button>
  );
}
